const ACTIVE_TAB_KEY = 'alea.shell.activeTab';
const SESSION_ENDED_AT_KEY = 'alea.shell.sessionEndedAt';

function storage(): Storage | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage;
}

/** Persist the active shell tab so a same-session reload can return to it. */
export function rememberActiveTab(tab: string): void {
  const store = storage();
  if (!store) return;
  const next = String(tab ?? '').trim();
  if (!next) {
    store.removeItem(ACTIVE_TAB_KEY);
    return;
  }
  store.setItem(ACTIVE_TAB_KEY, next);
}

/** Drop the saved tab so the next shell mount opens the dashboard. */
export function clearRememberedShellTab(): void {
  storage()?.removeItem(ACTIVE_TAB_KEY);
}

/** Stamp logout so the next auth mounts the dashboard (not the last tab). */
export function markShellSessionEnded(nowMs: number = Date.now()): void {
  const store = storage();
  if (!store) return;
  store.setItem(SESSION_ENDED_AT_KEY, String(nowMs));
  store.removeItem(ACTIVE_TAB_KEY);
}

/**
 * Resolve the tab after auth / shell mount.
 * - Fresh login / logout → dashboard (`fallback`)
 * - Reload while still logged in (no ended stamp) → last tab
 */
export function resolvePostLoginTab(fallback = 'overview'): string {
  const store = storage();
  if (!store) return fallback;

  const endedRaw = store.getItem(SESSION_ENDED_AT_KEY);
  if (endedRaw != null) {
    store.removeItem(SESSION_ENDED_AT_KEY);
    store.removeItem(ACTIVE_TAB_KEY);
    return fallback;
  }

  const rawTab = store.getItem(ACTIVE_TAB_KEY);
  const tab = (rawTab ?? '').trim();
  if (rawTab != null) store.removeItem(ACTIVE_TAB_KEY);
  return tab || fallback;
}

/** @deprecated Prefer resolvePostLoginTab — kept for call-site compatibility. */
export function consumeRememberedTab(fallback = 'overview'): string {
  return resolvePostLoginTab(fallback);
}

/** Full document reload after remembering the tab (for failed lazy chunks). */
export function reloadToRememberedTab(tab: string): void {
  rememberActiveTab(tab);
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
}
